// InventoryControl — مراقبة المخزون (3.4.8): أصناف + نقص مخزون + حركات تعديل باعتماد.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { hasActionPermission } from '../../utils/accessControl';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const TYPES = ['medication', 'farrier_material', 'other'];
const emptyItem = { item_type: 'medication', name: '', sku: '', unit: '', quantity: 0, min_stock: 0, lot_batch: '', expiry_date: '', supplier: '', purchase_price: '' };

export default function InventoryControl() {
  const { t } = useTranslation();
  const canManage = hasActionPermission('inventory-manage');
  const canApprove = hasActionPermission('inventory-approve');

  const [items, setItems] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [pending, setPending] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [itemDialog, setItemDialog] = useState(null); // {form}
  const [adjustDialog, setAdjustDialog] = useState(null); // {item, delta, reason}

  const load = useCallback(async () => {
    setError('');
    try {
      const [itemsRes, lowRes, adjRes] = await Promise.all([
        api.get('/inventory/items', { params: { item_type: typeFilter || undefined, q: search || undefined } }),
        api.get('/inventory/low-stock'),
        canApprove ? api.get('/inventory/adjustments', { params: { status: 'pending' } }) : Promise.resolve({ data: [] }),
      ]);
      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : itemsRes.data?.data || []);
      setLowStock(lowRes.data?.items || []);
      setPending(Array.isArray(adjRes.data) ? adjRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
  }, [typeFilter, search, canApprove]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const saveItem = async () => {
    try {
      const f = itemDialog.form;
      if (f.inventory_id) await api.put(`/inventory/items/${f.inventory_id}`, f);
      else await api.post('/inventory/items', f);
      setItemDialog(null);
      load();
    } catch (e) { setError(e?.response?.data?.message || e.message); }
  };

  const submitAdjust = async () => {
    try {
      await api.post(`/inventory/items/${adjustDialog.item.inventory_id}/adjust`, {
        delta: Number(adjustDialog.delta), reason: adjustDialog.reason,
      });
      setAdjustDialog(null);
      load();
    } catch (e) { setError(e?.response?.data?.message || e.message); }
  };

  const decide = async (adjustmentId, decision) => {
    try {
      await api.post(`/inventory/adjustments/${adjustmentId}/decide`, { decision });
      load();
    } catch (e) { setError(e?.response?.data?.message || e.message); }
  };

  const typeLabel = useMemo(() => ({
    medication: t('medication', 'دواء'),
    farrier_material: t('farrier_material', 'مادة بيطار'),
    other: t('other', 'أخرى'),
  }), [t]);

  return (
    <SmartPage>
      <SmartHeader title={t('inventory', 'المخزون')} subtitle={t('inventory_hint', 'الأدوية ومواد البيطار — كميات وحدود نقص وحركات معتمدة.')} />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {lowStock.length ? (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {t('low_stock_alert', 'أصناف بلغت حدّ النقص')}: {lowStock.map((x) => `${x.name} (${x.quantity}/${x.min_stock})`).join(' · ')}
        </Alert>
      ) : null}

      {/* أدوات */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label={t('type', 'النوع')} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ width: 200 }}>
          <MenuItem value="">{t('all', 'الكل')}</MenuItem>
          {TYPES.map((ty) => <MenuItem key={ty} value={ty}>{typeLabel[ty]}</MenuItem>)}
        </TextField>
        <TextField size="small" label={t('search', 'بحث')} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 260 }} />
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setItemDialog({ form: { ...emptyItem } })}
            sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
            {t('add_item', 'إضافة صنف')}
          </Button>
        ) : null}
      </Stack>

      {/* الجدول */}
      <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: C.cardSoft }}>
              {[t('name', 'الاسم'), t('type', 'النوع'), t('quantity', 'الكمية'), t('min_stock', 'حدّ النقص'), t('supplier', 'المورّد'), t('expiry', 'الصلاحية'), ''].map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: 900 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: C.muted, py: 3 }}>{t('no_items', 'لا أصناف')}</TableCell></TableRow>
            ) : items.map((it) => (
              <TableRow key={it.inventory_id} sx={Number(it.is_low) ? { bgcolor: C.roseSoft } : undefined}>
                <TableCell sx={{ fontWeight: 700 }}>{it.name}{it.lot_batch ? <Typography variant="caption" sx={{ color: C.muted, display: 'block' }}>{t('lot', 'تشغيلة')}: {it.lot_batch}</Typography> : null}</TableCell>
                <TableCell>{typeLabel[it.item_type] || it.item_type}</TableCell>
                <TableCell><b>{it.quantity}</b> {it.unit || ''}{Number(it.is_low) ? <Chip size="small" label={t('low', 'ناقص')} sx={{ ml: 1, bgcolor: C.rose, color: '#fff' }} /> : null}</TableCell>
                <TableCell>{it.min_stock}</TableCell>
                <TableCell>{it.supplier || '—'}</TableCell>
                <TableCell>{it.expiry_date ? String(it.expiry_date).slice(0, 10) : '—'}</TableCell>
                <TableCell align="right">
                  {canManage ? (
                    <>
                      <Tooltip title={t('adjust', 'تعديل كمية')}><IconButton size="small" onClick={() => setAdjustDialog({ item: it, delta: '', reason: '' })}><TuneIcon fontSize="small" /></IconButton></Tooltip>
                      <Button size="small" onClick={() => setItemDialog({ form: { ...it, expiry_date: it.expiry_date ? String(it.expiry_date).slice(0, 10) : '' } })}>{t('edit', 'تعديل')}</Button>
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* الحركات المعلّقة (للمعتمِد) */}
      {canApprove && pending.length ? (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>{t('pending_adjustments', 'حركات بانتظار الاعتماد')} ({pending.length})</Typography>
          <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 2 }}>
            <Table size="small">
              <TableBody>
                {pending.map((a) => (
                  <TableRow key={a.adjustment_id}>
                    <TableCell>{a.item_name}</TableCell>
                    <TableCell sx={{ fontWeight: 900, color: a.delta < 0 ? C.rose : C.green }}>{a.delta > 0 ? `+${a.delta}` : a.delta}</TableCell>
                    <TableCell>{a.reason || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="success" onClick={() => decide(a.adjustment_id, 'approve')}><CheckIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => decide(a.adjustment_id, 'reject')}><CloseIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : null}

      {/* حوار صنف */}
      <Dialog open={Boolean(itemDialog)} onClose={() => setItemDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{itemDialog?.form?.inventory_id ? t('edit_item', 'تعديل صنف') : t('add_item', 'إضافة صنف')}</DialogTitle>
        <DialogContent>
          {itemDialog ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {[
                ['name', t('name', 'الاسم'), 'text', 12],
                ['sku', 'SKU', 'text', 6],
                ['unit', t('unit', 'الوحدة'), 'text', 6],
                ['quantity', t('quantity', 'الكمية'), 'number', 6],
                ['min_stock', t('min_stock', 'حدّ النقص'), 'number', 6],
                ['lot_batch', t('lot', 'تشغيلة'), 'text', 6],
                ['expiry_date', t('expiry', 'الصلاحية'), 'date', 6],
                ['supplier', t('supplier', 'المورّد'), 'text', 6],
                ['purchase_price', t('purchase_price', 'سعر الشراء'), 'number', 6],
              ].map(([key, label, type, md]) => (
                <Grid item xs={12} md={md} key={key}>
                  <TextField
                    fullWidth size="small" type={type} label={label}
                    InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                    disabled={key === 'quantity' && Boolean(itemDialog.form.inventory_id)}
                    helperText={key === 'quantity' && itemDialog.form.inventory_id ? t('qty_via_adjust', 'تُعدَّل الكمية بحركة معتمدة') : undefined}
                    value={itemDialog.form[key] ?? ''}
                    onChange={(e) => setItemDialog((d) => ({ form: { ...d.form, [key]: e.target.value } }))}
                  />
                </Grid>
              ))}
              <Grid item xs={12} md={6}>
                <TextField select fullWidth size="small" label={t('type', 'النوع')} value={itemDialog.form.item_type}
                  onChange={(e) => setItemDialog((d) => ({ form: { ...d.form, item_type: e.target.value } }))}>
                  {TYPES.map((ty) => <MenuItem key={ty} value={ty}>{typeLabel[ty]}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog(null)}>{t('cancel', 'إلغاء')}</Button>
          <Button variant="contained" onClick={saveItem} disabled={!itemDialog?.form?.name?.trim()}>{t('save', 'حفظ')}</Button>
        </DialogActions>
      </Dialog>

      {/* حوار تعديل كمية */}
      <Dialog open={Boolean(adjustDialog)} onClose={() => setAdjustDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('adjust_quantity', 'طلب تعديل كمية')}</DialogTitle>
        <DialogContent>
          {adjustDialog ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: C.muted }}>{adjustDialog.item.name} — {t('current', 'الحالية')}: {adjustDialog.item.quantity}</Typography>
              <TextField type="number" size="small" label={t('delta', 'المقدار (+/−)')} value={adjustDialog.delta}
                onChange={(e) => setAdjustDialog((d) => ({ ...d, delta: e.target.value }))} />
              <TextField size="small" label={t('reason', 'السبب')} value={adjustDialog.reason}
                onChange={(e) => setAdjustDialog((d) => ({ ...d, reason: e.target.value }))} />
              <Typography variant="caption" sx={{ color: C.muted }}>{t('adjust_needs_approval', 'يبقى الطلب معلّقًا حتى الاعتماد.')}</Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog(null)}>{t('cancel', 'إلغاء')}</Button>
          <Button variant="contained" onClick={submitAdjust} disabled={!adjustDialog?.delta || Number(adjustDialog?.delta) === 0}>{t('submit', 'إرسال')}</Button>
        </DialogActions>
      </Dialog>
    </SmartPage>
  );
}
